import {convertGedcom, TopolaData} from "../utils/gedcom-utils"
import {IndiInfo, JsonGedcomData} from "../topola"
import {GedcomLanguage as Language} from "../model/gedcom-language"
import JSZip from "jszip"


/**
 * Loads data from GEDCOM file contents.
 */
export async function loadGedcom(hash: string, gedcom?: string, allLanguages?: Language[], images?: Map<string, string>): Promise<TopolaData> {
    try {
        const cachedData = localStorage.getItem(hash)
        if (cachedData)
            return JSON.parse(cachedData)
    } catch (e) {
        console.warn("Failed to load data from session storage: " + e)
    }
    if ((!gedcom) || (!allLanguages))
        throw new Error("Error loading data. Please upload your file again.")
    return prepareData(gedcom, hash, allLanguages, images)
}

function prepareData(gedcom: string, hash: string, allLanguages: Language[], images?: Map<string, string>): TopolaData {
    const gedcomData = convertGedcom(gedcom, allLanguages, images || new Map())
    try {
        localStorage.setItem(hash, JSON.stringify(gedcomData))  // Persists even after browser is closed, until explicitly removed
    } catch (e) {
        console.warn("Failed to store data in browser storage: " + e)
    }
    return gedcomData
}

/**
 * @deprecated
 * Fetches data from URL. Uses cors-anywhere if handleCors is true.
 */
export async function loadFromUrl(url: string, handleCors: boolean, allLanguages: Language[]) {
    try {
        const cachedData = sessionStorage.getItem(url)
        if (cachedData)
            return JSON.parse(cachedData)
    } catch (e) {
        console.warn("Failed to load data from session storage: " + e)
    }

    // handle GoogleDrive files
    const driveUrlMatch = url.match(/https:\/\/drive\.google\.com\/file\/d\/(.*)\/.*/,)
    if (driveUrlMatch) {
        url = `https://drive.google.com/uc?id=${driveUrlMatch[1]}&export=download`
    }

    const urlToFetch = handleCors ? "https://universal-cors-proxy.glitch.me/" + encodeURIComponent(url) : url
    const response = await fetch(urlToFetch)
    if (!response.ok)
        throw new Error(response.statusText)

    const contentType = response.headers.get("content-type")
    if (contentType && contentType.includes("application/zip")) {
        const data = await response.blob()
        const {gedcom, images} = await loadFile(data)
        return prepareData(gedcom, url, allLanguages, images)
    } else if (contentType && contentType.includes("application/json")) {
        const data = await response.json()
        return prepareData(data.contents, url, allLanguages, new Map())
    } else if (contentType && (contentType.includes("text/plain") || contentType.includes("text/html"))) {
        let data = await response.text()
        while (data.charCodeAt(0) > 127) {
            data = data.slice(1)
        }
        return prepareData(data, url, allLanguages, new Map())
    }
}

export async function loadFile(blob: Blob): Promise<{ gedcom: string, images: Map<string, string> }> {
    const fileHeader = await blob.slice(0, 2).text()
    if (fileHeader === "PK")
        return loadGedzip(blob)
    return {gedcom: await blob.text(), images: new Map()}
}

async function loadGedzip(blob: Blob): Promise<{ gedcom: string, images: Map<string, string> }> {
    const zip = await JSZip.loadAsync(blob)
    let gedcom: string | undefined
    const images = new Map<string, string>()
    for (const [filename, file] of Object.entries(zip.files)) {
        if (file.dir) continue
        if (filename.endsWith(".ged")) {
            if (gedcom)
                console.warn("Multiple GEDCOM files found in zip archive.")
            else
                gedcom = await file.async("string")
        } else {
            // For images or other files
            const blob = await file.async("blob")
            images.set(filename, URL.createObjectURL(blob))
        }
    }
    if (!gedcom)
        throw new Error("GEDCOM file not found in Zip archive.")
    return {gedcom, images}
}

/**
 * Returns a valid IndiInfo object,
 * either with the indi and generation or with an individual taken from the data and generation 0.
 */
export function getSelection(data: JsonGedcomData, selection?: IndiInfo): IndiInfo {
    // If ID is not set, or it doesn't exist in the data, use the first ID in the data.
    const id = selection && data.indis.some((i) => i.id === selection.id)
        ? selection.id
        : data.indis[0].id
    return {id, generation: selection?.generation || 0}
}